import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { api } from '../utils/api';
import { withColdStartRetry, isColdStartError } from '../utils/apiRetry';

// Initial state
const initialState = {
  expenses: [],
  categories: [],
  loading: false,
  error: null,
  coldStart: {
    isActive: false,
    message: '',
    detail: '',
    attempt: 0,
  },
};

// Action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_EXPENSES: 'SET_EXPENSES',
  ADD_EXPENSE: 'ADD_EXPENSE',
  UPDATE_EXPENSE: 'UPDATE_EXPENSE',
  DELETE_EXPENSE: 'DELETE_EXPENSE',
  SET_CATEGORIES: 'SET_CATEGORIES',
  ADD_CATEGORY: 'ADD_CATEGORY',
  UPDATE_CATEGORY: 'UPDATE_CATEGORY',
  DELETE_CATEGORY: 'DELETE_CATEGORY',
  SET_COLD_START: 'SET_COLD_START',
  CLEAR_COLD_START: 'CLEAR_COLD_START',
};

// Reducer
const expenseReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    case ACTIONS.SET_EXPENSES:
      return { ...state, expenses: action.payload, loading: false };
    case ACTIONS.ADD_EXPENSE:
      return { ...state, expenses: [action.payload, ...state.expenses], loading: false };
    case ACTIONS.UPDATE_EXPENSE:
      return {
        ...state,
        expenses: state.expenses.map(expense =>
          expense._id === action.payload._id ? action.payload : expense
        ),
        loading: false,
      };
    case ACTIONS.DELETE_EXPENSE:
      return {
        ...state,
        expenses: state.expenses.filter(expense => expense._id !== action.payload),
        loading: false,
      };
    case ACTIONS.SET_CATEGORIES:
      return { ...state, categories: action.payload, loading: false };
    case ACTIONS.ADD_CATEGORY:
      return { ...state, categories: [...state.categories, action.payload], loading: false };
    case ACTIONS.UPDATE_CATEGORY:
      return {
        ...state,
        categories: state.categories.map(category =>
          category._id === action.payload._id ? action.payload : category
        ),
        loading: false,
      };
    case ACTIONS.DELETE_CATEGORY:
      return {
        ...state,
        categories: state.categories.filter(category => category._id !== action.payload),
        loading: false,
      };
    case ACTIONS.SET_COLD_START:
      return {
        ...state,
        coldStart: {
          isActive: true,
          message: action.payload.message,
          detail: action.payload.detail,
          attempt: action.payload.attempt,
        },
      };
    case ACTIONS.CLEAR_COLD_START:
      return {
        ...state,
        coldStart: {
          isActive: false,
          message: '',
          detail: '',
          attempt: 0,
        },
      };
    default:
      return state;
  }
};

// Create context
const ExpenseContext = createContext();

// Custom hook to use the context
export const useExpenses = () => {
  const context = useContext(ExpenseContext);
  if (!context) {
    throw new Error('useExpenses must be used within an ExpenseProvider');
  }
  return context;
};

// Provider component
export const ExpenseProvider = ({ children }) => {
  const [state, dispatch] = useReducer(expenseReducer, initialState);

  // Set loading state
  const setLoading = (isLoading) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: isLoading });
  };

  // Set error state
  const setError = (error) => {
    dispatch({ type: ACTIONS.SET_ERROR, payload: error });
  };

  // Load expenses with cold start retry
  const loadExpenses = async (options = {}) => {
    setLoading(true);
    try {
      const data = await withColdStartRetry(
        () => api.getTransactions(options),
        {
          onProgress: (message, detail, attempt) => {
            dispatch({ 
              type: ACTIONS.SET_COLD_START, 
              payload: { message, detail, attempt } 
            });
          },
          onError: (message, detail) => {
            dispatch({ 
              type: ACTIONS.SET_COLD_START, 
              payload: { message, detail, attempt: 12 } 
            });
          },
        }
      );
      dispatch({ type: ACTIONS.CLEAR_COLD_START });
      dispatch({ type: ACTIONS.SET_EXPENSES, payload: data.transactions || data });
    } catch (err) {
      dispatch({ type: ACTIONS.CLEAR_COLD_START });
      setError('Failed to load expenses: ' + err.message);
    }
  };

  // Add expense
  const addExpense = async (expenseData) => {
    setLoading(true);
    try {
      const data = await api.createTransaction(expenseData);
      if (data.success) {
        dispatch({ type: ACTIONS.ADD_EXPENSE, payload: data.data });
        return data;
      } else {
        throw new Error(data.error || 'Failed to add expense');
      }
    } catch (err) {
      setError('Failed to add expense: ' + err.message);
      throw err;
    }
  };

  // Update expense
  const updateExpense = async (id, expenseData) => {
    setLoading(true);
    try {
      const data = await api.updateTransaction(id, expenseData);
      if (data.success) {
        dispatch({ type: ACTIONS.UPDATE_EXPENSE, payload: data.data });
        return data;
      } else {
        throw new Error(data.error || 'Failed to update expense');
      }
    } catch (err) {
      setError('Failed to update expense: ' + err.message);
      throw err;
    }
  };

  // Delete expense
  const deleteExpense = async (id) => {
    setLoading(true);
    try {
      const data = await api.deleteTransaction(id);
      if (data.success) {
        dispatch({ type: ACTIONS.DELETE_EXPENSE, payload: id });
        return data;
      } else {
        throw new Error(data.error || 'Failed to delete expense');
      }
    } catch (err) {
      setError('Failed to delete expense: ' + err.message);
      throw err;
    }
  };

  // Load categories with cold start retry
  const loadCategories = async () => {
    setLoading(true);
    try {
      const data = await withColdStartRetry(
        () => api.getCategories(),
        {
          onProgress: (message, detail, attempt) => {
            dispatch({ 
              type: ACTIONS.SET_COLD_START, 
              payload: { message, detail, attempt } 
            });
          },
          onError: (message, detail) => {
            dispatch({ 
              type: ACTIONS.SET_COLD_START, 
              payload: { message, detail, attempt: 12 } 
            });
          },
        }
      );
      dispatch({ type: ACTIONS.CLEAR_COLD_START });
      dispatch({ type: ACTIONS.SET_CATEGORIES, payload: data.categories || data });
    } catch (err) {
      dispatch({ type: ACTIONS.CLEAR_COLD_START });
      setError('Failed to load categories: ' + err.message);
    }
  };

  // Add category
  const addCategory = async (categoryData) => {
    setLoading(true);
    try {
      const data = await api.createCategory(categoryData);
      if (data.success) {
        dispatch({ type: ACTIONS.ADD_CATEGORY, payload: data.data });
        return data;
      } else {
        throw new Error(data.error || 'Failed to add category');
      }
    } catch (err) {
      setError('Failed to add category: ' + err.message);
      throw err;
    }
  };

  // Update category
  const updateCategory = async (id, categoryData) => {
    setLoading(true);
    try {
      const data = await api.updateCategory(id, categoryData);
      if (data.success) {
        dispatch({ type: ACTIONS.UPDATE_CATEGORY, payload: data.data });
        return data;
      } else {
        throw new Error(data.error || 'Failed to update category');
      }
    } catch (err) {
      setError('Failed to update category: ' + err.message);
      throw err;
    }
  };

  // Delete category
  const deleteCategory = async (id) => {
    setLoading(true);
    try {
      const data = await api.deleteCategory(id);
      if (data.success) {
        dispatch({ type: ACTIONS.DELETE_CATEGORY, payload: id });
        return data;
      } else {
        throw new Error(data.error || 'Failed to delete category');
      }
    } catch (err) {
      setError('Failed to delete category: ' + err.message);
      throw err;
    }
  };

  // Get expense stats
  const getExpenseStats = async (options = {}) => {
    try {
      const data = await api.getReports({ type: 'stats', ...options });
      return data;
    } catch (err) {
      setError('Failed to get expense stats: ' + err.message);
      throw err;
    }
  };

  // Get expense trends
  const getExpenseTrends = async (months = 6) => {
    try {
      const data = await api.getReports({ type: 'trends', months });
      return data;
    } catch (err) {
      setError('Failed to get expense trends: ' + err.message);
      throw err;
    }
  };

  // Get stats
  const getStats = () => {
    const totalExpenses = state.expenses.length;
    const thisMonthTotal = state.expenses
      .filter(expense => {
        const expenseDate = new Date(expense.date);
        const now = new Date();
        return (
          expenseDate.getMonth() === now.getMonth() &&
          expenseDate.getFullYear() === now.getFullYear()
        );
      })
      .reduce((sum, expense) => sum + expense.amount, 0);

    const thisMonthCount = state.expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      const now = new Date();
      return (
        expenseDate.getMonth() === now.getMonth() &&
        expenseDate.getFullYear() === now.getFullYear()
      );
    }).length;

    // Calculate top category
    const categorySums = {};
    state.expenses.forEach(expense => {
      if (expense.categoryName) {
        if (!categorySums[expense.categoryName]) {
          categorySums[expense.categoryName] = 0;
        }
        categorySums[expense.categoryName] += expense.amount;
      }
    });

    let topCategory = 'Other';
    let topCategoryAmount = 0;
    Object.entries(categorySums).forEach(([category, amount]) => {
      if (amount > topCategoryAmount) {
        topCategory = category;
        topCategoryAmount = amount;
      }
    });

    return {
      totalExpenses,
      thisMonthTotal,
      thisMonthCount,
      topCategory,
      topCategoryAmount,
      categorySums,
    };
  };

  // Context value
  const contextValue = {
    ...state,
    setLoading,
    setError,
    loadExpenses,
    addExpense,
    updateExpense,
    deleteExpense,
    loadCategories,
    addCategory,
    updateCategory,
    deleteCategory,
    getExpenseStats,
    getExpenseTrends,
    getStats,
  };

  return (
    <ExpenseContext.Provider value={contextValue}>
      {children}
    </ExpenseContext.Provider>
  );
};

export default ExpenseContext;
